/**
 * Delivery Date Picker für Kreutzers Shopify Store
 *
 * Features:
 * - Konfigurierbare Versandtage (Di, Mi, Do, Fr)
 * - Feiertags-Validierung via feiertage-api.de
 * - Fisch/Geflügel-spezifische Vorlaufzeiten
 * - Bundesland-basierte Validierung
 */

// =============================================================================
// KONSTANTEN
// =============================================================================

const BUNDESLAENDER = [
  { code: 'SL', name: 'Saarland' },
  { code: 'BW', name: 'Baden-Württemberg' },
  { code: 'BY', name: 'Bayern' },
  { code: 'BE', name: 'Berlin' },
  { code: 'BB', name: 'Brandenburg' },
  { code: 'HB', name: 'Bremen' },
  { code: 'HH', name: 'Hamburg' },
  { code: 'HE', name: 'Hessen' },
  { code: 'MV', name: 'Mecklenburg-Vorpommern' },
  { code: 'NI', name: 'Niedersachsen' },
  { code: 'NW', name: 'Nordrhein-Westfalen' },
  { code: 'RP', name: 'Rheinland-Pfalz' },
  { code: 'SN', name: 'Sachsen' },
  { code: 'ST', name: 'Sachsen-Anhalt' },
  { code: 'SH', name: 'Schleswig-Holstein' },
  { code: 'TH', name: 'Thüringen' }
];

// Warehouse ist im Saarland
const WAREHOUSE_BUNDESLAND = 'SL';

// Versandtage: Di=2, Mi=3, Do=4, Fr=5 (JavaScript: 0=So, 1=Mo, ..., 6=Sa)
const VALID_SHIPPING_DAYS = [2, 3, 4, 5]; // Dienstag bis Freitag

// Liefertage: Mi=3, Do=4, Fr=5, Sa=6
const VALID_DELIVERY_DAYS = [3, 4, 5, 6]; // Mittwoch bis Samstag

// Vorlauftage (Arbeitstage): Mo-Fr
const VALID_LEAD_DAYS = [1, 2, 3, 4, 5]; // Montag bis Freitag

// Konfiguration
const CONFIG = {
  deadlineRegular: '20:00',
  deadlineFish: '17:00',
  leadDaysRegular: 1,
  leadDaysFish: 2,
  maxOrdersPerDay: 50
};

// Wochentag-Namen
const WEEKDAY_NAMES = {
  0: 'Sonntag',
  1: 'Montag',
  2: 'Dienstag',
  3: 'Mittwoch',
  4: 'Donnerstag',
  5: 'Freitag',
  6: 'Samstag'
};

const WEEKDAY_NAMES_SHORT = {
  0: 'So',
  1: 'Mo',
  2: 'Di',
  3: 'Mi',
  4: 'Do',
  5: 'Fr',
  6: 'Sa'
};

// =============================================================================
// FEIERTAGE API
// =============================================================================

class HolidayService {
  constructor() {
    this.cache = {};
  }

  /**
   * Holt Feiertage für ein Bundesland und Jahr
   * @param {string} bundeslandCode - z.B. 'SL', 'BY'
   * @param {number} year - z.B. 2025
   * @returns {Promise<Object>} Feiertage als Objekt mit Datum-Strings
   */
  async getHolidays(bundeslandCode, year) {
    const cacheKey = `holidays_${year}_${bundeslandCode}`;

    // 1. Prüfe In-Memory Cache
    if (this.cache[cacheKey]) {
      return this.cache[cacheKey];
    }

    // 2. Prüfe localStorage
    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        this.cache[cacheKey] = parsed;
        return parsed;
      }
    } catch (e) {
      console.warn('localStorage nicht verfügbar:', e);
    }

    // 3. Lade von API
    try {
      const response = await fetch(
        `https://feiertage-api.de/api/?jahr=${year}&nur_land=${bundeslandCode}`
      );

      if (!response.ok) {
        throw new Error(`API Fehler: ${response.status}`);
      }

      const data = await response.json();

      // Transformiere zu Set von Datum-Strings für schnelle Lookups
      const holidayDates = {};
      for (const [name, info] of Object.entries(data)) {
        holidayDates[info.datum] = name;
      }

      // Cache speichern
      this.cache[cacheKey] = holidayDates;
      try {
        localStorage.setItem(cacheKey, JSON.stringify(holidayDates));
      } catch (e) {
        console.warn('localStorage speichern fehlgeschlagen:', e);
      }

      return holidayDates;
    } catch (error) {
      console.error('Feiertage laden fehlgeschlagen:', error);
      return {};
    }
  }

  /**
   * Prüft ob ein Datum ein Feiertag ist
   * @param {Date} date
   * @param {string} bundeslandCode
   * @returns {Promise<boolean>}
   */
  async isHoliday(date, bundeslandCode) {
    const year = date.getFullYear();
    const holidays = await this.getHolidays(bundeslandCode, year);
    const dateString = this.formatDate(date);
    return dateString in holidays;
  }

  /**
   * Formatiert Date zu YYYY-MM-DD String
   * @param {Date} date
   * @returns {string}
   */
  formatDate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}

// =============================================================================
// DELIVERY DATE PICKER
// =============================================================================

class DeliveryDatePicker {
  constructor(options = {}) {
    this.holidayService = new HolidayService();
    this.container = null;
    this.hasFishPoultry = options.hasFishPoultry || false;
    this.onDateSelect = options.onDateSelect || null;
    this.maxDaysToShow = options.maxDaysToShow || 4;

    // Cache für geladene Feiertage
    this.holidaysCache = {
      warehouse: {},
      destination: {}
    };

    // Session Storage Keys
    this.storageKeys = {
      bundesland: 'delivery_picker_bundesland',
      deliveryDate: 'delivery_picker_date'
    };

    // Lade gecachte Werte aus Session Storage
    this.selectedBundesland = this.loadFromSession('bundesland') || options.defaultBundesland || null;
    this.selectedDeliveryDate = this.loadFromSession('deliveryDate') || null;
  }

  /**
   * Lädt einen Wert aus dem Session Storage
   * @param {string} key - 'bundesland' oder 'deliveryDate'
   * @returns {string|null}
   */
  loadFromSession(key) {
    try {
      return sessionStorage.getItem(this.storageKeys[key]);
    } catch (e) {
      console.warn('Session Storage nicht verfügbar:', e);
      return null;
    }
  }

  /**
   * Speichert einen Wert im Session Storage
   * @param {string} key - 'bundesland' oder 'deliveryDate'
   * @param {string} value
   */
  saveToSession(key, value) {
    try {
      if (value) {
        sessionStorage.setItem(this.storageKeys[key], value);
      } else {
        sessionStorage.removeItem(this.storageKeys[key]);
      }
    } catch (e) {
      console.warn('Session Storage speichern fehlgeschlagen:', e);
    }
  }

  // ===========================================================================
  // VALIDIERUNGS-FUNKTIONEN
  // ===========================================================================

  /**
   * Prüft ob ein Datum ein gültiger Versandtag ist
   * Versandtage: Di-Fr, kein Saarland-Feiertag
   * @param {Date} date
   * @returns {Promise<boolean>}
   */
  async isValidShippingDay(date) {
    const dayOfWeek = date.getDay();

    // Prüfe Wochentag (Di-Fr)
    if (!VALID_SHIPPING_DAYS.includes(dayOfWeek)) {
      return false;
    }

    // Prüfe Saarland-Feiertag (Warehouse)
    const isHoliday = await this.holidayService.isHoliday(date, WAREHOUSE_BUNDESLAND);
    return !isHoliday;
  }

  /**
   * Prüft ob ein Datum ein gültiger Liefertag ist
   * Liefertage: Mi-Sa, kein Feiertag im Ziel-Bundesland
   * @param {Date} date
   * @param {string} bundeslandCode - Ziel-Bundesland
   * @returns {Promise<boolean>}
   */
  async isValidDeliveryDay(date, bundeslandCode) {
    const dayOfWeek = date.getDay();

    // Prüfe Wochentag (Mi-Sa)
    if (!VALID_DELIVERY_DAYS.includes(dayOfWeek)) {
      return false;
    }

    // Prüfe Feiertag im Ziel-Bundesland
    const isHoliday = await this.holidayService.isHoliday(date, bundeslandCode);
    return !isHoliday;
  }

  /**
   * Prüft ob ein Datum ein gültiger Vorlauftag ist
   * Vorlauftage: Mo-Fr, kein Saarland-Feiertag
   * @param {Date} date
   * @returns {Promise<boolean>}
   */
  async isValidLeadDay(date) {
    const dayOfWeek = date.getDay();

    // Prüfe Wochentag (Mo-Fr)
    if (!VALID_LEAD_DAYS.includes(dayOfWeek)) {
      return false;
    }

    // Prüfe Saarland-Feiertag
    const isHoliday = await this.holidayService.isHoliday(date, WAREHOUSE_BUNDESLAND);
    return !isHoliday;
  }

  // ===========================================================================
  // BERECHNUNGS-FUNKTIONEN
  // ===========================================================================

  /**
   * Berechnet Versandtag aus Liefertag
   * Versandtag = Liefertag - 1 Tag
   * @param {Date} deliveryDate
   * @returns {Date}
   */
  calculateShippingDate(deliveryDate) {
    const shippingDate = new Date(deliveryDate);
    shippingDate.setDate(shippingDate.getDate() - 1);
    return shippingDate;
  }

  /**
   * Zählt Arbeitstage zurück vom Versandtag
   * @param {Date} fromDate - Startdatum (Versandtag)
   * @param {number} count - Anzahl Arbeitstage zurück
   * @returns {Promise<Date>} Deadline-Datum
   */
  async countLeadDaysBack(fromDate, count) {
    let currentDate = new Date(fromDate);
    let daysFound = 0;

    while (daysFound < count) {
      // Einen Tag zurück
      currentDate.setDate(currentDate.getDate() - 1);

      // Prüfe ob gültiger Vorlauftag
      const isValid = await this.isValidLeadDay(currentDate);
      if (isValid) {
        daysFound++;
      }
    }

    return currentDate;
  }

  /**
   * Berechnet Deadline für einen Versandtag
   * @param {Date} shippingDate
   * @param {boolean} hasFish - Hat der Warenkorb Fisch/Geflügel?
   * @returns {Promise<{date: Date, time: string, dateTime: Date}>}
   */
  async calculateDeadline(shippingDate, hasFish) {
    const leadDays = hasFish ? CONFIG.leadDaysFish : CONFIG.leadDaysRegular;
    const deadlineTime = hasFish ? CONFIG.deadlineFish : CONFIG.deadlineRegular;

    // Finde den Deadline-Tag
    const deadlineDate = await this.countLeadDaysBack(shippingDate, leadDays);

    // Erstelle DateTime mit Uhrzeit
    const [hours, minutes] = deadlineTime.split(':').map(Number);
    const deadlineDateTime = new Date(deadlineDate);
    deadlineDateTime.setHours(hours, minutes, 0, 0);

    return {
      date: deadlineDate,
      time: deadlineTime,
      dateTime: deadlineDateTime
    };
  }

  /**
   * Prüft ob die Deadline für einen Liefertag bereits überschritten ist
   * @param {Date} deliveryDate
   * @param {boolean} hasFish
   * @returns {Promise<boolean>}
   */
  async isDeadlinePassed(deliveryDate, hasFish) {
    const shippingDate = this.calculateShippingDate(deliveryDate);
    const deadline = await this.calculateDeadline(shippingDate, hasFish);

    const now = new Date();
    return now > deadline.dateTime;
  }

  /**
   * Holt die nächsten gültigen Liefertage
   * @param {Date} startDate - Ab welchem Datum suchen
   * @param {number} count - Wie viele Tage zurückgeben
   * @param {string} bundeslandCode - Ziel-Bundesland
   * @param {boolean} hasFish - Hat der Warenkorb Fisch/Geflügel?
   * @returns {Promise<Array<{date: Date, shippingDate: Date, deadline: Object, isAvailable: boolean}>>}
   */
  async getValidDeliveryDays(startDate, count, bundeslandCode, hasFish) {
    const validDays = [];
    let currentDate = new Date(startDate);
    let daysChecked = 0;
    const maxDaysToCheck = 60; // Sicherheitslimit

    while (validDays.length < count && daysChecked < maxDaysToCheck) {
      // Prüfe ob gültiger Liefertag
      const isValidDelivery = await this.isValidDeliveryDay(currentDate, bundeslandCode);

      if (isValidDelivery) {
        const shippingDate = this.calculateShippingDate(currentDate);
        const isValidShipping = await this.isValidShippingDay(shippingDate);

        if (isValidShipping) {
          const deadline = await this.calculateDeadline(shippingDate, hasFish);
          const now = new Date();
          const isAvailable = now < deadline.dateTime;

          validDays.push({
            date: new Date(currentDate),
            shippingDate: shippingDate,
            deadline: deadline,
            isAvailable: isAvailable
          });
        }
      }

      // Nächster Tag
      currentDate.setDate(currentDate.getDate() + 1);
      daysChecked++;
    }

    return validDays;
  }

  /**
   * Holt den ersten verfügbaren Liefertag
   * @param {string} bundeslandCode
   * @param {boolean} hasFish
   * @returns {Promise<Object|null>}
   */
  async getFirstAvailableDeliveryDay(bundeslandCode, hasFish) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const validDays = await this.getValidDeliveryDays(today, 10, bundeslandCode, hasFish);
    return validDays.find(day => day.isAvailable) || null;
  }

  // ===========================================================================
  // UI-FUNKTIONEN
  // ===========================================================================

  /**
   * Rendert den Date Picker in einen Container
   * @param {HTMLElement} container
   */
  async renderDatePicker(container) {
    this.container = container;

    // Berechne min/max Datum für Kalender
    const today = new Date();
    const minDate = new Date(today);
    minDate.setDate(minDate.getDate() + 2); // Mindestens 2 Tage in der Zukunft
    const maxDate = new Date(today);
    maxDate.setMonth(maxDate.getMonth() + 3); // Maximal 3 Monate in der Zukunft

    const minDateStr = this.holidayService.formatDate(minDate);
    const maxDateStr = this.holidayService.formatDate(maxDate);

    // Erstelle Grundstruktur
    container.innerHTML = `
      <div class="delivery-picker">
        <div class="delivery-picker__header">
          <span class="delivery-picker__icon">🚚</span>
          <span class="delivery-picker__title">Wählen Sie Ihren Liefertag</span>
        </div>

        <div class="delivery-picker__bundesland">
          <label for="bundesland-select">Lieferadresse:</label>
          <select id="bundesland-select" class="delivery-picker__select">
            <option value="">Bundesland wählen...</option>
            ${BUNDESLAENDER.map(bl =>
              `<option value="${bl.code}" ${bl.code === this.selectedBundesland ? 'selected' : ''}>${bl.name}</option>`
            ).join('')}
          </select>
        </div>

        <div class="delivery-picker__dates" id="delivery-dates">
          <p class="delivery-picker__placeholder">Bitte wählen Sie zuerst ein Bundesland.</p>
        </div>

        <div class="delivery-picker__calendar" id="delivery-calendar" style="display: none;">
          <label class="delivery-picker__calendar-label">Oder wählen Sie ein anderes Datum:</label>
          <div class="delivery-picker__calendar-input-wrapper">
            <input
              type="date"
              id="delivery-date-input"
              class="delivery-picker__date-input"
              min="${minDateStr}"
              max="${maxDateStr}"
            >
            <button type="button" id="apply-date-btn" class="delivery-picker__apply-btn">Datum prüfen</button>
          </div>
          <p class="delivery-picker__calendar-hint">Lieferung nur Mi-Sa möglich (kein Feiertag)</p>
          <div id="date-validation-message" class="delivery-picker__validation-message"></div>
        </div>

        <div class="delivery-picker__info" id="delivery-info" style="display: none;">
          <div class="delivery-picker__deadline" id="deadline-display"></div>
          <div class="delivery-picker__shipping" id="shipping-display"></div>
        </div>

        ${this.hasFishPoultry ? `
          <div class="delivery-picker__fish-notice">
            <span class="delivery-picker__fish-icon">🐟</span>
            <span>Ihr Warenkorb enthält Fisch/Geflügel. Die Bestellfrist endet früher.</span>
          </div>
        ` : ''}
      </div>
    `;

    // Event Listener für Bundesland
    const select = container.querySelector('#bundesland-select');
    select.addEventListener('change', (e) => {
      this.selectedBundesland = e.target.value;
      this.saveToSession('bundesland', e.target.value);
      // Reset delivery date when Bundesland changes (Feiertage können unterschiedlich sein)
      this.selectedDeliveryDate = null;
      this.saveToSession('deliveryDate', null);
      this.updateDateButtons();
    });

    // Event Listener für Kalender
    const applyBtn = container.querySelector('#apply-date-btn');
    const dateInput = container.querySelector('#delivery-date-input');

    applyBtn.addEventListener('click', () => {
      this.validateAndSelectCustomDate(dateInput.value);
    });

    dateInput.addEventListener('change', () => {
      // Validierungsnachricht zurücksetzen bei Änderung
      this.clearValidationMessage();
    });

    // Initial laden wenn Bundesland vorausgewählt
    if (this.selectedBundesland) {
      await this.updateDateButtons();
    }
  }

  /**
   * Validiert und wählt ein benutzerdefiniertes Datum aus
   * @param {string} dateStr - Datum im Format YYYY-MM-DD
   */
  async validateAndSelectCustomDate(dateStr) {
    if (!dateStr) {
      this.showValidationMessage('Bitte wählen Sie ein Datum aus.', 'error');
      return;
    }

    if (!this.selectedBundesland) {
      this.showValidationMessage('Bitte wählen Sie zuerst ein Bundesland aus.', 'error');
      return;
    }

    const selectedDate = new Date(dateStr + 'T00:00:00');
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Prüfe ob Datum in der Vergangenheit
    if (selectedDate <= today) {
      this.showValidationMessage('Das Datum muss in der Zukunft liegen.', 'error');
      return;
    }

    // Prüfe ob gültiger Liefertag (Mi-Sa)
    const dayOfWeek = selectedDate.getDay();
    if (!VALID_DELIVERY_DAYS.includes(dayOfWeek)) {
      const validDaysStr = 'Mittwoch, Donnerstag, Freitag oder Samstag';
      this.showValidationMessage(`Lieferung nur an ${validDaysStr} möglich.`, 'error');
      return;
    }

    // Prüfe auf Feiertag im Ziel-Bundesland
    const isDeliveryHoliday = await this.holidayService.isHoliday(selectedDate, this.selectedBundesland);
    if (isDeliveryHoliday) {
      this.showValidationMessage('An diesem Tag ist ein Feiertag in Ihrem Bundesland. Bitte wählen Sie einen anderen Tag.', 'error');
      return;
    }

    // Berechne Versandtag und prüfe
    const shippingDate = this.calculateShippingDate(selectedDate);
    const isValidShipping = await this.isValidShippingDay(shippingDate);
    if (!isValidShipping) {
      this.showValidationMessage('Für diesen Liefertag ist kein Versand möglich (Versandtag fällt auf Wochenende oder Feiertag im Saarland).', 'error');
      return;
    }

    // Berechne Deadline und prüfe
    const deadline = await this.calculateDeadline(shippingDate, this.hasFishPoultry);
    const now = new Date();
    if (now > deadline.dateTime) {
      const deadlineDay = WEEKDAY_NAMES[deadline.date.getDay()];
      const deadlineDateStr = `${deadline.date.getDate()}.${deadline.date.getMonth() + 1}.`;
      this.showValidationMessage(`Die Bestellfrist für diesen Tag ist bereits abgelaufen (${deadlineDay}, ${deadlineDateStr} um ${deadline.time} Uhr).`, 'error');
      return;
    }

    // Alles OK - Datum auswählen
    this.showValidationMessage('Datum ist verfügbar!', 'success');

    // Deselektiere alle Buttons
    this.container.querySelectorAll('.delivery-picker__date-btn--selected').forEach(btn => {
      btn.classList.remove('delivery-picker__date-btn--selected');
    });

    // Speichere Auswahl
    this.selectedDeliveryDate = dateStr;
    this.saveToSession('deliveryDate', dateStr);

    // Aktualisiere Info-Anzeige
    this.updateDeadlineDisplay(
      dateStr,
      this.holidayService.formatDate(shippingDate),
      deadline.dateTime
    );

    // Speichere in Cart Attributes (angepasste Version für custom date)
    await this.saveCustomDateToCartAttributes(dateStr, shippingDate, deadline);

    // Callback aufrufen
    if (this.onDateSelect) {
      this.onDateSelect({
        deliveryDate: dateStr,
        shippingDate: this.holidayService.formatDate(shippingDate),
        deadline: deadline.dateTime,
        bundesland: this.selectedBundesland
      });
    }
  }

  /**
   * Speichert ein benutzerdefiniertes Datum in Cart Attributes
   */
  async saveCustomDateToCartAttributes(deliveryDate, shippingDate, deadline) {
    const bundeslandName = BUNDESLAENDER.find(bl => bl.code === this.selectedBundesland)?.name || this.selectedBundesland;

    const deadlineStr = `${deadline.date.getFullYear()}-${String(deadline.date.getMonth() + 1).padStart(2, '0')}-${String(deadline.date.getDate()).padStart(2, '0')} ${deadline.time}`;

    const attributes = {
      'Liefertag': deliveryDate,
      'Versandtag': this.holidayService.formatDate(shippingDate),
      'Bundesland': bundeslandName,
      'Bestellfrist': deadlineStr,
      'Enthält Fisch/Geflügel': this.hasFishPoultry ? 'Ja' : 'Nein'
    };

    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes })
      });

      if (!response.ok) {
        throw new Error('Cart update fehlgeschlagen');
      }

      console.log('Cart Attributes gespeichert:', attributes);
    } catch (error) {
      console.error('Fehler beim Speichern der Cart Attributes:', error);
    }
  }

  /**
   * Zeigt eine Validierungsnachricht an
   * @param {string} message
   * @param {string} type - 'error' oder 'success'
   */
  showValidationMessage(message, type) {
    const messageEl = this.container.querySelector('#date-validation-message');
    if (messageEl) {
      messageEl.textContent = message;
      messageEl.className = `delivery-picker__validation-message delivery-picker__validation-message--${type}`;
    }
  }

  /**
   * Löscht die Validierungsnachricht
   */
  clearValidationMessage() {
    const messageEl = this.container.querySelector('#date-validation-message');
    if (messageEl) {
      messageEl.textContent = '';
      messageEl.className = 'delivery-picker__validation-message';
    }
  }

  /**
   * Aktualisiert die Datums-Buttons basierend auf Bundesland
   */
  async updateDateButtons() {
    const datesContainer = this.container.querySelector('#delivery-dates');
    const calendarContainer = this.container.querySelector('#delivery-calendar');

    if (!this.selectedBundesland) {
      datesContainer.innerHTML = '<p class="delivery-picker__placeholder">Bitte wählen Sie zuerst ein Bundesland.</p>';
      if (calendarContainer) calendarContainer.style.display = 'none';
      this.hideInfo();
      return;
    }

    // Loading State
    datesContainer.innerHTML = '<p class="delivery-picker__loading">Lade verfügbare Termine...</p>';

    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const validDays = await this.getValidDeliveryDays(
        today,
        this.maxDaysToShow,
        this.selectedBundesland,
        this.hasFishPoultry
      );

      if (validDays.length === 0) {
        datesContainer.innerHTML = '<p class="delivery-picker__error">Keine verfügbaren Liefertermine gefunden.</p>';
        // Zeige trotzdem den Kalender
        if (calendarContainer) calendarContainer.style.display = 'block';
        return;
      }

      // Render Buttons
      datesContainer.innerHTML = `
        <p class="delivery-picker__quick-select-label">Nächste verfügbare Termine:</p>
        <div class="delivery-picker__buttons">
          ${validDays.map((day, index) => {
            const dateStr = this.holidayService.formatDate(day.date);
            const dayName = WEEKDAY_NAMES_SHORT[day.date.getDay()];
            const dayNum = day.date.getDate();
            const monthNum = day.date.getMonth() + 1;

            return `
              <button
                type="button"
                class="delivery-picker__date-btn ${!day.isAvailable ? 'delivery-picker__date-btn--unavailable' : ''} ${dateStr === this.selectedDeliveryDate ? 'delivery-picker__date-btn--selected' : ''}"
                data-date="${dateStr}"
                data-shipping="${this.holidayService.formatDate(day.shippingDate)}"
                data-deadline="${day.deadline.dateTime.toISOString()}"
                ${!day.isAvailable ? 'disabled' : ''}
              >
                <span class="delivery-picker__day-name">${dayName}</span>
                <span class="delivery-picker__day-date">${dayNum}.${monthNum}.</span>
                ${!day.isAvailable ? '<span class="delivery-picker__status">Frist abgelaufen</span>' : ''}
              </button>
            `;
          }).join('')}
        </div>
      `;

      // Zeige Kalender für weitere Daten
      if (calendarContainer) {
        calendarContainer.style.display = 'block';
        this.clearValidationMessage();
      }

      // Event Listener für Buttons
      const buttons = datesContainer.querySelectorAll('.delivery-picker__date-btn:not([disabled])');
      buttons.forEach(btn => {
        btn.addEventListener('click', (e) => {
          this.selectDate(btn);
          this.clearValidationMessage();
          // Lösche Eingabe im Kalender wenn Button geklickt
          const dateInput = this.container.querySelector('#delivery-date-input');
          if (dateInput) dateInput.value = '';
        });
      });

      // Prüfe ob ein gecachtes Datum vorhanden ist
      if (this.selectedDeliveryDate) {
        // Versuche das gecachte Datum in den Buttons zu finden
        const cachedButton = datesContainer.querySelector(`.delivery-picker__date-btn[data-date="${this.selectedDeliveryDate}"]:not([disabled])`);

        if (cachedButton) {
          // Gecachtes Datum ist in den Quick-Select Buttons - auswählen
          this.selectDate(cachedButton);
        } else {
          // Gecachtes Datum ist ein Kalenderdatum - im Kalender wiederherstellen
          const dateInput = this.container.querySelector('#delivery-date-input');
          if (dateInput) {
            dateInput.value = this.selectedDeliveryDate;
            // Automatisch validieren und Info anzeigen
            this.validateAndSelectCustomDate(this.selectedDeliveryDate);
          }
        }
      } else {
        // Kein gecachtes Datum - ersten verfügbaren Tag auswählen
        const firstAvailable = datesContainer.querySelector('.delivery-picker__date-btn:not([disabled])');
        if (firstAvailable) {
          this.selectDate(firstAvailable);
        }
      }

    } catch (error) {
      console.error('Fehler beim Laden der Termine:', error);
      datesContainer.innerHTML = '<p class="delivery-picker__error">Fehler beim Laden der Termine. Bitte versuchen Sie es erneut.</p>';
      // Zeige trotzdem den Kalender als Fallback
      if (calendarContainer) calendarContainer.style.display = 'block';
    }
  }

  /**
   * Wählt ein Datum aus
   * @param {HTMLElement} button
   */
  selectDate(button) {
    // Entferne vorherige Auswahl
    this.container.querySelectorAll('.delivery-picker__date-btn--selected').forEach(btn => {
      btn.classList.remove('delivery-picker__date-btn--selected');
    });

    // Markiere neuen Button
    button.classList.add('delivery-picker__date-btn--selected');

    // Speichere Auswahl
    this.selectedDeliveryDate = button.dataset.date;
    this.saveToSession('deliveryDate', button.dataset.date);
    const shippingDate = button.dataset.shipping;
    const deadline = new Date(button.dataset.deadline);

    // Aktualisiere Info-Anzeige
    this.updateDeadlineDisplay(this.selectedDeliveryDate, shippingDate, deadline);

    // Speichere in Cart Attributes
    this.saveToCartAttributes();

    // Callback aufrufen
    if (this.onDateSelect) {
      this.onDateSelect({
        deliveryDate: this.selectedDeliveryDate,
        shippingDate: shippingDate,
        deadline: deadline,
        bundesland: this.selectedBundesland
      });
    }
  }

  /**
   * Aktualisiert die Deadline-Anzeige
   */
  updateDeadlineDisplay(deliveryDate, shippingDate, deadline) {
    const infoContainer = this.container.querySelector('#delivery-info');
    const deadlineDisplay = this.container.querySelector('#deadline-display');
    const shippingDisplay = this.container.querySelector('#shipping-display');

    if (!deliveryDate) {
      infoContainer.style.display = 'none';
      return;
    }

    infoContainer.style.display = 'block';

    // Formatiere Deadline
    const deadlineDay = WEEKDAY_NAMES[deadline.getDay()];
    const deadlineDateStr = `${deadline.getDate()}.${deadline.getMonth() + 1}.`;
    const deadlineTime = `${String(deadline.getHours()).padStart(2, '0')}:${String(deadline.getMinutes()).padStart(2, '0')}`;

    deadlineDisplay.innerHTML = `
      <span class="delivery-picker__info-icon">⏰</span>
      <span>Bestellfrist: <strong>${deadlineDay}, ${deadlineDateStr} um ${deadlineTime} Uhr</strong></span>
    `;

    // Formatiere Versandtag
    const shipDate = new Date(shippingDate + 'T00:00:00');
    const shipDay = WEEKDAY_NAMES[shipDate.getDay()];
    const shipDateStr = `${shipDate.getDate()}.${shipDate.getMonth() + 1}.`;

    shippingDisplay.innerHTML = `
      <span class="delivery-picker__info-icon">📦</span>
      <span>Versand: <strong>${shipDay}, ${shipDateStr}</strong></span>
    `;
  }

  /**
   * Versteckt die Info-Anzeige
   */
  hideInfo() {
    const infoContainer = this.container.querySelector('#delivery-info');
    if (infoContainer) {
      infoContainer.style.display = 'none';
    }
  }

  // ===========================================================================
  // CART INTEGRATION
  // ===========================================================================

  /**
   * Speichert die Auswahl in Cart Attributes
   */
  async saveToCartAttributes() {
    if (!this.selectedDeliveryDate || !this.selectedBundesland) {
      return;
    }

    const button = this.container.querySelector('.delivery-picker__date-btn--selected');
    if (!button) return;

    const shippingDate = button.dataset.shipping;
    const deadline = new Date(button.dataset.deadline);
    const bundeslandName = BUNDESLAENDER.find(bl => bl.code === this.selectedBundesland)?.name || this.selectedBundesland;

    // Formatiere Deadline für Anzeige
    const deadlineStr = `${deadline.getFullYear()}-${String(deadline.getMonth() + 1).padStart(2, '0')}-${String(deadline.getDate()).padStart(2, '0')} ${String(deadline.getHours()).padStart(2, '0')}:${String(deadline.getMinutes()).padStart(2, '0')}`;

    const attributes = {
      'Liefertag': this.selectedDeliveryDate,
      'Versandtag': shippingDate,
      'Bundesland': bundeslandName,
      'Bestellfrist': deadlineStr,
      'Enthält Fisch/Geflügel': this.hasFishPoultry ? 'Ja' : 'Nein'
    };

    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes })
      });

      if (!response.ok) {
        throw new Error('Cart update fehlgeschlagen');
      }

      console.log('Cart Attributes gespeichert:', attributes);
    } catch (error) {
      console.error('Fehler beim Speichern der Cart Attributes:', error);
    }
  }

  /**
   * Setzt das Fisch/Geflügel Flag und aktualisiert die Anzeige
   * @param {boolean} hasFish
   */
  async setHasFishPoultry(hasFish) {
    if (this.hasFishPoultry !== hasFish) {
      this.hasFishPoultry = hasFish;
      if (this.container && this.selectedBundesland) {
        await this.updateDateButtons();
      }
    }
  }

  /**
   * Setzt das Bundesland und aktualisiert die Anzeige
   * @param {string} bundeslandCode
   */
  async setBundesland(bundeslandCode) {
    this.selectedBundesland = bundeslandCode;

    const select = this.container?.querySelector('#bundesland-select');
    if (select) {
      select.value = bundeslandCode;
    }

    if (this.container) {
      await this.updateDateButtons();
    }
  }
}

// =============================================================================
// EXPORT / GLOBAL
// =============================================================================

// Für globalen Zugriff
window.DeliveryDatePicker = DeliveryDatePicker;
window.HolidayService = HolidayService;
window.BUNDESLAENDER = BUNDESLAENDER;

// Fallback-Init für Cart Updates (wenn inline Script nicht ausgeführt wird, z.B. empty → non-empty cart)
document.addEventListener('cart:updated', function() {
  setTimeout(function() {
    document.querySelectorAll('[data-delivery-picker]').forEach(function(container) {
      if (container.deliveryPicker) return;
      container.dispatchEvent(new Event('delivery-picker:init'));
      // Manuell initialisieren falls das inline Script nicht lief
      if (!container.deliveryPicker && typeof window.DeliveryDatePicker !== 'undefined') {
        var hasFish = container.dataset.hasFishPoultry === 'true';
        var isCompact = container.dataset.compact === 'true';
        var isInline = container.dataset.inline === 'true';
        var saved = container.dataset.savedBundesland || null;
        var bl = saved ? (window.BUNDESLAENDER.find(function(b) { return b.name === saved; }) || {}).code || null : null;
        var picker = new DeliveryDatePicker({
          hasFishPoultry: hasFish,
          defaultBundesland: bl,
          maxDaysToShow: 4
        });
        picker.renderDatePicker(container).then(function() {
          var el = container.querySelector('.delivery-picker');
          if (el) {
            if (isCompact) el.classList.add('delivery-picker--compact');
            if (isInline) el.classList.add('delivery-picker--inline');
          }
        });
        container.deliveryPicker = picker;
      }
    });
  }, 150);
});

// Export für Module
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { DeliveryDatePicker, HolidayService, BUNDESLAENDER };
}
